/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useState
} from 'react';
import JsonView from 'react-pretty-json';
import {useDispatch, useSelector} from 'react-redux';
import {Button, ButtonGroup} from '@material-ui/core';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import isObject from 'lodash.isobject';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject, IdReport} from '@ion/core/src/types/entities';
import {copyToClipBoard} from '@ion/core/src/utils/BrowserUtils';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {CopyIcon} from '@ion/dls/src/icons/CopyIcon';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsAlt, dlsColor, dlsLayout} from '@ion/dls/src/theme/theme';
import {Color, DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {toggleUserPrompt} from '../../store/actions/user-prompt';
import {
  devicesViewSelector
} from '../../store/selectors/devices';
import LinuxDeviceFiles from './LinuxDeviceFiles';
import useVbusAccess from './useVbusAccess';

enum ID_REPORT_SECTION {
  OTA_CAPABILITIES,
  FILE_TABLE,
  DEVICE_SERVICES,
  EXTENSIONS,
  STANDARD_EXTENSIONS,
  RADIO_STATE_EXTENSION,
  LINUX_DEVICE_FILES,
  OTA_STATUS,
  FAILSAFE_STATUS,
  DEVICE_HEALTH_STATUS,
  UBOOT_RECOVERY,
  EDGE_APP,
  RAW_HEX
}

enum ID_REPORT_VIEW {
  RAW_VIEW = 'raw',
  JSON_VIEW = 'json'
}

type OmissionsMap = Map<ID_REPORT_SECTION, any>;
type ShowSectionMap = Map<ID_REPORT_SECTION, boolean>;

export interface IdReportTabType {
  selectedTab: Number;
  tabIndex: Number;
  idReportInfo: GenericObject;
  isMDT?: boolean;
};

export interface PaperWrapperProps {
  classes?: any;
  children?: ReactNode;
}

export const NoData = ({classes}: PaperWrapperProps) => {
  return (
    <Paper className={classes.noData}>
      {translate('admin-devices:view.manufacturing.noDataAvailable')}
    </Paper>
  );
};

export const PaperWrapper = ({children, classes}: PaperWrapperProps) => {
  return (
    <Paper className={classes.idReportPaper}>
      <MenuList>
        <MenuItem className={classes.listText} disableRipple>
          {children}
        </MenuItem>
      </MenuList>
    </Paper>
  );
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  formSection : {
    ...dlsAlt('typography', 'cta3'),
    fontWeight    : 700,
    color         : dlsColor('grey1'),
    letterSpacing : '0',
    padding       : '20px 0 10px 0'
  },
  flexStyle : {
    display : 'flex',
    height  : 'calc(100vh - 260px)'
  },
  leftContainer : {
    flex            : 1,
    backgroundColor : dlsColor('white'),
    marginTop       : '21px',
    marginRight     : '10px',
    padding         : '20px',
    width           : '30%',
    overflowY       : 'auto'
  },
  rightContainer : {
    width           : '68%',
    backgroundColor : dlsColor('white'),
    borderRradius   : '5px',
    marginTop       : '21px',
    padding         : '0px 10px',
    overflowY       : 'auto'
  },
  paraStyle : {
    margin : '10px 4px 20px 6px'
  },
  idReportColor : {
    borderBottom : `1px solid ${dlsColor('aaBlue')}`,
    color        : dlsColor('black')
  },
  copybtn : {
    margin    : '-25px 0px',
    position  : 'sticky',
    textAlign : 'right',
    top       : 0,
    zIndex    : 1
  },
  headerItem : {
    ...dlsAlt('typography', 'label1'),
    fontWeight    : '700',
    color         : dlsColor('grey1'),
    fontSize      : '14px',
    letterSpacing : 0,
    padding       : '5px'
  },
  bodyItem : {
    ...dlsAlt('typography', 'label2'),
    color         : dlsColor('grey1'),
    fontSize      : '14px',
    letterSpacing : 0,
    padding       : '5px'
  },
  listText : {
    display    : 'block',
    cursor     : 'auto',
    userSelect : 'unset',
    '&:hover'  : {
      backgroundColor : 'rgb(255 255 255) !important'
    }
  },
  divPara : {
    whiteSpace : 'pre-wrap'
  },
  paperStyle : {
    display   : 'inline-block',
    boxShadow : 'none'
  },
  noData : {
    width          : '100%',
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    height         : '400px',
    boxShadow      : 'none'
  },
  idReportPaper : {
    display       : 'inline-block',
    marginTop     : '40px',
    overflow      : 'auto',
    width         : '100%',
    boxShadow     : 'none',
    pointerEvents : 'auto'
  },
  idReportList : {
    listStyleType : 'none',
    lineHeight    : '25px',
    textTransform : 'uppercase'
  },
  serviceStyle : {
    marginBottom : '30px'
  },
  listStyle : {
    listStyleType : 'square'
  },
  itemStyle : {
    ...dlsAlt('typography', 'label1'),
    color        : dlsColor('grey2'),
    marginBottom : '20px'
  },
  detailsTab : {
    height : '100%'
  }
}));

interface SectionMenuProps {
  omissions: OmissionsMap;
  selectedSection: ID_REPORT_SECTION,
  onSectionClick: (newSectionIndex: ID_REPORT_SECTION) => void;
}

/**
 * Displays a menu list of the id report sections available for the user to view.
 */
const SectionsMenu = memo(({
  omissions,
  selectedSection,
  onSectionClick
}: SectionMenuProps): JSX.Element => {
  const classes             = useStyles();
  const sections            = useMemo(() => getSections(getLabels()), []);
  const showSectionMappings = useMemo(() => buildShowSectionMappings(sections, omissions), [sections, omissions]);

  const onMenuItemClick = useCallback((e) => {
    onSectionClick(Number(e.target?.id));
  }, []);

  const menuItems = [];
  sections.forEach((sectionIndex, label) => {
    if (showSectionMappings.get(sectionIndex)) {
      const itemId = `${label}-${sectionIndex}`;
      menuItems.push((
        <MenuItem key={itemId} id={sectionIndex} onClick={onMenuItemClick}
          className={selectedSection === sectionIndex
            ? classnames(classes.itemStyle, classes.idReportColor)
            : classes.itemStyle}>
          {label}
        </MenuItem>
      ));
    }
  });
  return (
    <MenuList>
      {menuItems}
    </MenuList>
  );
});

/**
 * IdReportTab component that displays the Device manufacturing
 * data for the selected Device Details tab.
 */
export const IdReportTab = ({
  idReportInfo: idReport,
  isMDT = false,
  tabIndex = 0,
  selectedTab = 0
}: IdReportTabType) => {
  const classes                           = useStyles();
  const dispatch                          = useDispatch();
  const isCalampInteralRole               = useVbusAccess();
  const {selected}                        = useSelector(devicesViewSelector);
  const deviceIdReport                    = selected?.idReport;
  const idReportRaw                       = deviceIdReport?.device?.idReportRaw;
  const [idReportValue, setIdReportValue] = useState(ID_REPORT_SECTION.OTA_CAPABILITIES);
  const [idReportTab, setIdReportTab]     = useState(ID_REPORT_VIEW.RAW_VIEW);
  const isRaw                             = isEqual(idReportTab, ID_REPORT_VIEW.RAW_VIEW);
  const isIdReportRawTab                  = isEqual(idReportValue, ID_REPORT_SECTION.RAW_HEX);

  const omissions = useMemo(() =>
    getOmissions(idReport, isMDT, isCalampInteralRole), [idReport, isMDT, isCalampInteralRole]);

  const reportValues: IdReportData = useMemo(() =>
    getIdReportInfo(idReport, deviceIdReport, idReportTab), [idReport, idReportTab]);

  const {
    otaCapability,
    fileTable,
    deviceServices,
    extensions,
    standardExtensions,
    radioStateExtension,
    otaStatus,
    linuxDeviceFiles,
    failsafeStatus,
    deviceHealthStatus,
    ubootRecovery,
    edgeApp
  } = useMemo(() => getReportValues(reportValues), [reportValues]);

  const onSectionClick = useCallback((newSectionIndex: ID_REPORT_SECTION): void => {
    setIdReportValue(newSectionIndex);
  }, []);

  const copyValuesToClipboard = useCallback(() => {
    copyToClipBoard(isRaw ? idReportRaw || '' : safeJSONStringify(reportValues[idReportValue] ?? {}));
    dispatch(toggleUserPrompt({
      message          : translate('common:clipboard.copySuccess'),
      autoHideDuration : 1000
    }));
  }, [idReportValue, reportValues]);

  const toggleRaw = useCallback(function(e) {
    setIdReportTab(e.target.innerText.toLowerCase());
  }, []);

  return (
    <div id={`detailsTab_${tabIndex}`} className={classes.detailsTab} hidden={selectedTab !== tabIndex}>
      <div className={classes.flexStyle}>
        <div className={classes.leftContainer}>
          <Paper className={classes.paperStyle}>
            <SectionsMenu omissions={omissions}
              selectedSection={idReportValue}
              onSectionClick={onSectionClick} />
          </Paper>
        </div>
        <div className={classes.rightContainer}>
          <div className={classes.copybtn}>
            <button onClick={copyValuesToClipboard} disabled={isIdReportRawTab && !idReportRaw}>
              <CopyIcon size={IconSize.medium} color={Color.grey3}/>
            </button>
          </div>
          {idReportValue === ID_REPORT_SECTION.OTA_CAPABILITIES && (
            otaCapability ? (
              <PaperWrapper classes={classes}>
                {Object.entries(otaCapability).map(([otaCapabilityKey, otaCapabilityValue]) => (
                  <ul className={classes.idReportList} key={otaCapabilityKey}>
                    <li>{otaCapabilityKey}
                      <ul className={classes.idReportList}>
                        {Object.entries(otaCapabilityValue).map(([otaCapabilitySubKey, otaCapabilitySubValue]) => (
                          <li key={otaCapabilitySubKey}>{otaCapabilitySubKey}
                            <ul className={classes.idReportList}>
                              {Object.entries(otaCapabilitySubValue).map(([key, value]) => (
                                <li key={key}>{key}
                                  <ol>
                                    {(value).map((record, index) => (
                                      <li key={index}>{record?.name}</li>
                                    ))}
                                  </ol>
                                </li>
                              ))}
                            </ul>
                          </li>
                        ))}
                      </ul>
                    </li>
                  </ul>
                ))}
              </PaperWrapper>
            ) : (<NoData classes={classes} />)
          )}
          {idReportValue === ID_REPORT_SECTION.FILE_TABLE && (
            fileTable ? (
              <PaperWrapper classes={classes}>
                {Object.entries(fileTable).map(([fileTableKey, fileTableValue]) => (
                  <ul className={classes.idReportList} key={fileTableKey}>
                    <li className={classes.listText}>{`${fileTableKey}: `}
                      {typeof fileTableValue !== 'object' ? fileTableValue
                        : Object.entries(fileTableValue).map(([fileTableSubKey, fileTableSubValue]) => (
                          <ul className={classes.idReportList} key={fileTableSubKey}>
                            <li>{fileTableSubKey}: {typeof fileTableSubValue !== 'object' ? fileTableSubValue
                              : <ol>{
                                Object.entries(fileTableSubValue).map(([label, value]) => (
                                  <li key={label}>{value}</li>
                                ))
                              }
                              </ol>
                            }</li>
                          </ul>
                        ))
                      }
                    </li>
                  </ul>
                ))}
              </PaperWrapper>
            ) : (<NoData classes={classes} />)
          )}
          {idReportValue === ID_REPORT_SECTION.DEVICE_SERVICES && (
            deviceServices ? (
              <PaperWrapper classes={classes}>
                {Object.entries(deviceServices).map(([deviceServicesKey, deviceServicesList]) => (
                  <ul className={classes.idReportList} key={deviceServicesKey}>
                    <li>{deviceServicesKey}
                      <ul className={classes.idReportList}>
                        {deviceServicesList.map((deviceServicesValue, indx) => (
                          <div className={classes.serviceStyle} key={indx}>
                            <p>{`${translate('admin-devices:view.idReport.service')} ${indx + 1} :`}</p>
                            <ul> {
                              Object.entries(deviceServicesValue).map(([deviceServicesSubKey,
                                deviceServicesSubValue]) => (
                                <li key={deviceServicesSubKey}>
                                  {deviceServicesSubKey} : {Array.isArray(deviceServicesSubValue)
                                    ? <ul className={classes.listStyle}>{
                                      deviceServicesSubValue.map((value, index) => (
                                        <li key={index}>{value}</li>
                                      ))
                                    }
                                    </ul>
                                    : deviceServicesSubValue}</li>
                              ))
                            }
                            </ul>
                          </div>
                        ))}
                      </ul>
                    </li>
                  </ul>
                ))}
              </PaperWrapper>
            ) : (<NoData classes={classes} />)
          )}
          {idReportValue === ID_REPORT_SECTION.EXTENSIONS && (
            extensions ? (
              <PaperWrapper classes={classes}>
                {Object.entries(extensions).map(([extensionsKey, extensionsValue]) => (
                  <ul className={classes.idReportList} key={extensionsKey}>
                    <li>{extensionsKey}
                      <ul className={classes.idReportList}>
                        {Array.isArray(extensionsValue)
                          ? <li>
                            {extensionsValue[0].split(',').map((value, index) => {
                              return <li key={index}>{value}</li>;
                            })}
                          </li>
                          : <li>{extensionsValue}</li>}
                      </ul>
                    </li>
                  </ul>
                ))}
              </PaperWrapper>
            ) : (<NoData classes={classes} />)
          )}
          {idReportValue === ID_REPORT_SECTION.STANDARD_EXTENSIONS && (
            standardExtensions ? (
              <PaperWrapper classes={classes}>
                {Object.entries(standardExtensions).map(([extensionKey, extensionValue]) => (
                  <ul key={extensionKey} className={classes.idReportList}>
                    <li>
                      <span>{extensionKey}</span>
                      <ul className={classes.idReportList}>
                        {!isNil(extensionValue) &&
                          Object.entries(extensionValue)?.map(([label, value]) => (
                            <li key={label}>
                              <span>{label}</span>
                              {(!isNil(value) &&
                                isObject(value)) ? (
                                  <ul className={classes.idReportList}>
                                    {Object.entries(value)?.map?.(([key, name]) => (
                                      <li key={key}>{key} : {name}</li>
                                    ))}
                                  </ul>
                                ) : ` : ${(value)}`}
                            </li>
                          ))}
                      </ul>
                    </li>
                  </ul>
                ))}
              </PaperWrapper>
            ) : (<NoData classes={classes} />)
          )}
          {idReportValue === ID_REPORT_SECTION.RADIO_STATE_EXTENSION && (
            radioStateExtension ? (
              <PaperWrapper classes={classes}>
                {Object.entries(radioStateExtension)?.map(([radioExtensionKey, radioExtensionValue]) => (
                  <ul className={classes.idReportList} key={radioExtensionKey}>
                    <li className={classes.listText}>{`${radioExtensionKey} : `}
                      {!isNil(radioExtensionValue) && (
                        typeof radioExtensionValue !== 'object' ? String(radioExtensionValue)
                          : Object.entries(radioExtensionValue).map(([key, value]) => (
                            <ul className={classes.idReportList} key={key}>
                              <li>{key} : {value}</li>
                            </ul>
                          ))
                      )
                      }
                    </li>
                  </ul>
                ))}
              </PaperWrapper>
            ) : (<NoData classes={classes} />)
          )}
          {idReportValue === ID_REPORT_SECTION.LINUX_DEVICE_FILES && (
            linuxDeviceFiles ? (
              <LinuxDeviceFiles files={linuxDeviceFiles} />
            ) : (<NoData classes={classes} />)
          )}
          {idReportValue === ID_REPORT_SECTION.OTA_STATUS && (
            otaStatus ? (
              <PaperWrapper classes={classes}>
                {Object.entries(otaStatus).map(([otaStatusKey, otaStatusValue]) => (
                  <ul className={classes.idReportList} key={otaStatusKey}>
                    <li className={classes.listText}>{otaStatusKey} :
                      {typeof otaStatusValue !== 'object' ? otaStatusValue
                        : Object.entries(otaStatusValue).map(([key, value]) => (
                          <ul className={classes.idReportList} key={key}>
                            <li>{key} : {value}</li>
                          </ul>
                        ))
                      }
                    </li>
                  </ul>
                ))}
              </PaperWrapper>
            ) : (<NoData classes={classes} />)
          )}
          {idReportValue === ID_REPORT_SECTION.FAILSAFE_STATUS && (
            failsafeStatus ? (
              <PaperWrapper classes={classes}>
                {Object.entries(failsafeStatus).map(([failsafeStatusKey, failsafeStatusValue]) => (
                  <ul className={classes.idReportList} key={failsafeStatusKey}>
                    <li className={classes.listText}>{failsafeStatusKey} :
                      {typeof failsafeStatusValue !== 'object' ? String(failsafeStatusValue)
                        : Object.entries(failsafeStatusValue).map(([key, value]) => (
                          <ul className={classes.idReportList} key={key}>
                            <li>{key} : {value}</li>
                          </ul>
                        ))
                      }
                    </li>
                  </ul>
                ))}
              </PaperWrapper>
            ) : (<NoData classes={classes} />)
          )}
          {idReportValue === ID_REPORT_SECTION.DEVICE_HEALTH_STATUS && (
            deviceHealthStatus ? (
              <PaperWrapper classes={classes}>
                {Object.entries(deviceHealthStatus).map(([deviceHealthStatusKey, deviceHealthStatusValue]) => (
                  <ul className={classes.idReportList} key={deviceHealthStatusKey}>
                    <li className={classes.listText}>{deviceHealthStatusKey} :
                      {typeof deviceHealthStatusValue !== 'object' ? String(deviceHealthStatusValue)
                        : Object.entries(deviceHealthStatusValue).map(([key, value]) => (
                          <ul className={classes.idReportList} key={key}>
                            <li>{key} : {value}</li>
                          </ul>
                        ))
                      }
                    </li>
                  </ul>
                ))}
              </PaperWrapper>
            ) : (<NoData classes={classes} />)
          )}
          {idReportValue === ID_REPORT_SECTION.UBOOT_RECOVERY && (
            ubootRecovery ? (
              <PaperWrapper classes={classes}>
                {Object.entries(ubootRecovery).map(([ubootRecoveryKey, ubootRecoveryValue]) => (
                  <ul className={classes.idReportList} key={ubootRecoveryKey}>
                    <li className={classes.listText}>{ubootRecoveryKey} :
                      {typeof ubootRecoveryValue !== 'object' ? String(ubootRecoveryValue)
                        : Object.entries(ubootRecoveryValue).map(([key, value]) => (
                          <ul className={classes.idReportList} key={key}>
                            <li>{key} : {value}</li>
                          </ul>
                        ))
                      }
                    </li>
                  </ul>
                ))}
              </PaperWrapper>
            ) : (<NoData classes={classes} />)
          )}
          {idReportValue === ID_REPORT_SECTION.EDGE_APP && (
            edgeApp ? (
              <PaperWrapper classes={classes}>
                {Object.entries(edgeApp).map(([edgeAppKey, edgeAppValue]) => (
                  <ul className={classes.idReportList} key={edgeAppKey}>
                    <li className={classes.listText}>{edgeAppKey} :
                      {typeof edgeAppValue !== 'object' ? String(edgeAppValue)
                        : Object.entries(edgeAppValue).map(([key, value]) => (
                          <ul className={classes.idReportList} key={key}>
                            <li>{key} : {value}</li>
                          </ul>
                        ))
                      }
                    </li>
                  </ul>
                ))}
              </PaperWrapper>
            ) : (<NoData classes={classes} />)
          )}
          {idReportValue === ID_REPORT_SECTION.RAW_HEX && (
            <div>
              <ButtonGroup style={{paddingTop: 10, textAlign: 'left'}} className={classes.copybtn} disableElevation variant='outlined' color='primary'>
                <Button defaultChecked
                  disabled={!idReportRaw}
                  style={{height: 35}}
                  variant={isRaw ? 'contained' : 'outlined'}
                  onClick={toggleRaw} color='primary'>
                  {translate('admin-devices:view.idReport.raw')}
                </Button>
                <Button disabled={!idReportRaw}
                  onClick={toggleRaw}
                  style={{height: 35}}
                  variant={isEqual(idReportTab, ID_REPORT_VIEW.JSON_VIEW) ? 'contained' : 'outlined'}
                  color='primary'>
                  {translate('admin-devices:view.idReport.json')}
                </Button>
              </ButtonGroup>
              {idReportRaw ? (<>
                <ConditionalRender condition={isRaw}>
                  <div style={{wordWrap: 'break-word', paddingTop: 60}} className={classes.idReportList}>{idReportRaw}</div>
                </ConditionalRender>
                <ConditionalRender condition={isEqual(idReportTab, ID_REPORT_VIEW.JSON_VIEW)}>
                  <form style={{height: '100%', paddingTop: 60}}>
                    <pre><JsonView json={deviceIdReport || ''}/></pre>
                  </form>
                </ConditionalRender>
              </>) : (<NoData classes={classes} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Determines whether or not to show a section based on the given omissions list.
 *
 * @param omissions the map of sections to hide from displaying
 * @param sectionIndex the section index to be checked
 * @returns true if the section should be shown, false otherwise
 */
const showSection = (omissions: OmissionsMap, sectionIndex: ID_REPORT_SECTION): boolean => {
  return !!(!omissions.has(sectionIndex) || omissions.get(sectionIndex));
};

/**
 * Builds the ordered sections.
 *
 * @param labels the translated section labels
 * @returns section data with keys being the label and values being the index of the section
 */
const getSections = (labels: string[]): GenericObject => {
  const sections = new Map<string, number>();
  sections.set(labels[ID_REPORT_SECTION.OTA_CAPABILITIES], ID_REPORT_SECTION.OTA_CAPABILITIES);
  sections.set(labels[ID_REPORT_SECTION.FILE_TABLE], ID_REPORT_SECTION.FILE_TABLE);
  sections.set(labels[ID_REPORT_SECTION.DEVICE_SERVICES], ID_REPORT_SECTION.DEVICE_SERVICES);
  sections.set(labels[ID_REPORT_SECTION.EXTENSIONS], ID_REPORT_SECTION.EXTENSIONS);
  sections.set(labels[ID_REPORT_SECTION.STANDARD_EXTENSIONS], ID_REPORT_SECTION.STANDARD_EXTENSIONS);
  sections.set(labels[ID_REPORT_SECTION.RADIO_STATE_EXTENSION], ID_REPORT_SECTION.RADIO_STATE_EXTENSION);
  sections.set(labels[ID_REPORT_SECTION.LINUX_DEVICE_FILES], ID_REPORT_SECTION.LINUX_DEVICE_FILES);
  sections.set(labels[ID_REPORT_SECTION.OTA_STATUS], ID_REPORT_SECTION.OTA_STATUS);
  sections.set(labels[ID_REPORT_SECTION.FAILSAFE_STATUS], ID_REPORT_SECTION.FAILSAFE_STATUS);
  sections.set(labels[ID_REPORT_SECTION.DEVICE_HEALTH_STATUS], ID_REPORT_SECTION.DEVICE_HEALTH_STATUS);
  sections.set(labels[ID_REPORT_SECTION.UBOOT_RECOVERY], ID_REPORT_SECTION.UBOOT_RECOVERY);
  sections.set(labels[ID_REPORT_SECTION.EDGE_APP], ID_REPORT_SECTION.EDGE_APP);
  sections.set(labels[ID_REPORT_SECTION.RAW_HEX], ID_REPORT_SECTION.RAW_HEX);
  return sections;
};

/**
 * Gets the section labels and stores them in the correct order.
 *
 * @returns a string array of section labels
 */
const getLabels = (): string[] => {
  const {
    otaCapabilities,
    fileTable,
    deviceServices,
    extensions,
    standardExtensions,
    radioStateExtension,
    otaStatus,
    linuxDeviceFiles,
    failsafeStatus,
    deviceHealthStatus,
    ubootRecovery,
    edgeApp,
    rawHex
  } = translate('admin-devices:view.idReport', {returnObjects: true});
  return [
    otaCapabilities,
    fileTable,
    deviceServices,
    extensions,
    standardExtensions,
    radioStateExtension,
    linuxDeviceFiles,
    otaStatus,
    failsafeStatus,
    deviceHealthStatus,
    ubootRecovery,
    edgeApp,
    rawHex
  ];
};

/**
 * Generates the section titles to be hidden.
 *
 * @param idReport the id report information
 * @param isMDT if the id report is for an MDT device
 * @returns a map of section titles to hid from being displayed
 */
const getOmissions = (
  {
    linuxDeviceFiles,
    failsafeStatus,
    deviceHealthStatus,
    ubootRecovery,
    edgeApp,
    radioStateExtension
  }: IdReport = {},
  isMDT = false,
  isCalampInteralRole = false
): OmissionsMap => {
  const omissions = new Map();
  omissions.set(ID_REPORT_SECTION.RADIO_STATE_EXTENSION, radioStateExtension);
  omissions.set(ID_REPORT_SECTION.LINUX_DEVICE_FILES, linuxDeviceFiles);
  omissions.set(ID_REPORT_SECTION.FAILSAFE_STATUS, failsafeStatus);
  omissions.set(ID_REPORT_SECTION.DEVICE_HEALTH_STATUS, deviceHealthStatus);
  omissions.set(ID_REPORT_SECTION.UBOOT_RECOVERY, ubootRecovery);
  omissions.set(ID_REPORT_SECTION.EDGE_APP, edgeApp);

  if (isMDT) {
    omissions.set(ID_REPORT_SECTION.DEVICE_SERVICES, null);
    omissions.set(ID_REPORT_SECTION.STANDARD_EXTENSIONS, null);
    omissions.set(ID_REPORT_SECTION.LINUX_DEVICE_FILES, null);
    omissions.set(ID_REPORT_SECTION.FAILSAFE_STATUS, null);
    omissions.set(ID_REPORT_SECTION.DEVICE_HEALTH_STATUS, null);
    omissions.set(ID_REPORT_SECTION.UBOOT_RECOVERY, null);
  }

  if (!isCalampInteralRole) {
    omissions.set(ID_REPORT_SECTION.RAW_HEX, null);
  }
  return omissions;
};

/**
 * Builds a single map to help the component determine if a given section should be shown or hidden
 * in the menu list.
 *
 * @param potentialSections the object of potential sections to be displayed
 * @param omissions the map of omissions based on component flags
 * @returns a map linking the section index to a boolean of whether or not to display the section
 */
const buildShowSectionMappings = (potentialSections: GenericObject, omissions: OmissionsMap): ShowSectionMap => {
  const showSectionTitleMap = new Map();
  potentialSections.forEach((value: ID_REPORT_SECTION) => {
    showSectionTitleMap.set(value, true);
  });
  omissions.forEach((_, key: ID_REPORT_SECTION) => {
    showSectionTitleMap.set(key, showSection(omissions, key));
  });
  return showSectionTitleMap;
};

type IdReportData = {
  [key in ID_REPORT_SECTION]?: GenericObject;
}

/**
 * Safely destructures and returns the id report information.
 *
 * @param idReport the id report info for the device
 * @returns id report info
 */
const getIdReportInfo = (idReport: IdReport = {}, deviceIdReport, idReportTab: string): IdReportData => {
  const {
    otaCapability,
    fileTable,
    files,
    deviceServices,
    extensions,
    standardExtensions,
    radioStateExtension,
    otaStatus,
    linuxDeviceFiles,
    failsafeStatus,
    deviceHealthStatus,
    ubootRecovery,
    edgeApp
  } = idReport;
  return {
    [ID_REPORT_SECTION.OTA_CAPABILITIES]      : otaCapability,
    [ID_REPORT_SECTION.FILE_TABLE]            : fileTable || files,
    [ID_REPORT_SECTION.DEVICE_SERVICES]       : deviceServices,
    [ID_REPORT_SECTION.EXTENSIONS]            : extensions,
    [ID_REPORT_SECTION.STANDARD_EXTENSIONS]   : standardExtensions,
    [ID_REPORT_SECTION.RADIO_STATE_EXTENSION] : radioStateExtension,
    [ID_REPORT_SECTION.OTA_STATUS]            : otaStatus,
    [ID_REPORT_SECTION.LINUX_DEVICE_FILES]    : linuxDeviceFiles,
    [ID_REPORT_SECTION.FAILSAFE_STATUS]       : failsafeStatus,
    [ID_REPORT_SECTION.DEVICE_HEALTH_STATUS]  : deviceHealthStatus,
    [ID_REPORT_SECTION.UBOOT_RECOVERY]        : ubootRecovery,
    [ID_REPORT_SECTION.EDGE_APP]              : edgeApp,
    [ID_REPORT_SECTION.RAW_HEX]               : isEqual(idReportTab, ID_REPORT_VIEW.RAW_VIEW)
      ? {raw: deviceIdReport?.device?.idReportRaw} : deviceIdReport
  };
};

/**
 * Helper to generate the individual report values for use in the component.
 *
 * @param idReportValues the object containing all the report values
 * @returns an object with individual report values
 */
const getReportValues = (idReportValues: IdReportData = {}): IdReport => ({
  otaCapability       : idReportValues[ID_REPORT_SECTION.OTA_CAPABILITIES],
  fileTable           : idReportValues[ID_REPORT_SECTION.FILE_TABLE],
  deviceServices      : idReportValues[ID_REPORT_SECTION.DEVICE_SERVICES],
  extensions          : idReportValues[ID_REPORT_SECTION.EXTENSIONS],
  standardExtensions  : idReportValues[ID_REPORT_SECTION.STANDARD_EXTENSIONS],
  radioStateExtension : idReportValues[ID_REPORT_SECTION.RADIO_STATE_EXTENSION],
  otaStatus           : idReportValues[ID_REPORT_SECTION.OTA_STATUS],
  linuxDeviceFiles    : idReportValues[ID_REPORT_SECTION.LINUX_DEVICE_FILES],
  failsafeStatus      : idReportValues[ID_REPORT_SECTION.FAILSAFE_STATUS],
  deviceHealthStatus  : idReportValues[ID_REPORT_SECTION.DEVICE_HEALTH_STATUS],
  ubootRecovery       : idReportValues[ID_REPORT_SECTION.UBOOT_RECOVERY],
  edgeApp             : idReportValues[ID_REPORT_SECTION.EDGE_APP]
});

export default IdReportTab;
